import { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon, Search2Icon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Fade,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { getParcelasPagas, getNotaFiscal } from 'api/clients';
import { AccountContext } from 'components/AccountContext';
import ChildHeader from 'components/Dashboard/Main/ChildHeader';
import SadIcon from 'images/sadicon2.png';
import { ReactComponent as NotFoundWoman } from 'images/not-found-error-woman.svg';

function getMonthName(monthNumber) {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[monthNumber - 1] || '';
}

function BaixarNotaFiscalButton({ contrato, pacienteContratoParcelasId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const response = await getNotaFiscal(contrato, pacienteContratoParcelasId);
      if (!response.ok) {
        throw new Error('Erro ao baixar nota fiscal');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pacienteContratoParcelasId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [contrato, pacienteContratoParcelasId]);

  return (
    <>
      {error && (
        <Text color={'red.500'} fontSize={'xs'} mt={2}>
          Não foi possível baixar a nota fiscal
        </Text>
      )}
      <Button
        mt={2}
        size={'xs'}
        variant="outline"
        rounded={'full'}
        colorScheme="orange"
        borderColor={'#5AB2AE'}
        color={'#5AB2AE'}
        rightIcon={<DownloadIcon />}
        onClick={handleDownload}
        isLoading={loading}
        disabled={loading || error}
      >
        Baixar Nota Fiscal
      </Button>
    </>
  );
}

function DrawerSelectAno({ disclosure, setValue, anosOptions }) {
  const [ano, setAno] = useState(null);

  const currentYear = new Date().getFullYear();
  const lastFiveYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Drawer {...disclosure} placement="bottom">
      <DrawerOverlay />
      <DrawerContent roundedTop={32} color={'#585858'} p={4}>
        <DrawerCloseButton top={4} right={4} />
        <DrawerHeader fontSize={16} textAlign={'center'}>
          Filtrar ano
        </DrawerHeader>

        <DrawerBody>
          <Flex flexDirection={'column'} gap={4}>
            <Text fontWeight={'bold'}>Selecione o ano que deseja filtrar</Text>

            <FormControl>
              <FormLabel>Ano</FormLabel>
              <Select variant="filled" value={ano} onChange={(event) => setAno(event.target.value)} placeholder="Selecione uma opção">
                {anosOptions?.length
                  ? anosOptions.map((ano) => (
                      <option key={ano} value={ano}>
                        {ano}
                      </option>
                    ))
                  : lastFiveYears.map((ano) => (
                      <option key={ano} value={ano}>
                        {ano}
                      </option>
                    ))}
              </Select>
            </FormControl>
          </Flex>
        </DrawerBody>

        <DrawerFooter mt={20}>
          <Flex flexDirection={'column'} gap={4} width={'100%'}>
            <Text fontSize={'sm'} textAlign={'center'}>
              Você só pode selecionar um ano por vez
            </Text>
            <Button
              isFullWidth
              disabled={!ano}
              variant={'solid'}
              bgColor={'#5AB2AE'}
              color={'white'}
              rounded={'xl'}
              onClick={() => {
                setValue(ano);
                disclosure.onClose();
              }}
            >
              Filtrar
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function DrawerSelectMes({ disclosure, setValue }) {
  const [meses, setMeses] = useState([]);
  const [showMeses, setShowMeses] = useState(false);

  return (
    <Drawer {...disclosure} placement="bottom">
      <DrawerOverlay />
      <DrawerContent roundedTop={32} color={'#585858'} p={4}>
        <DrawerCloseButton top={4} right={4} />
        <DrawerHeader fontSize={16} textAlign={'center'}>
          Filtrar mês
        </DrawerHeader>

        <DrawerBody>
          <Flex flexDirection={'column'} gap={4}>
            <Text fontWeight={'bold'}>Selecione o mês que deseja filtrar</Text>

            <FormControl>
              <FormLabel>Mês</FormLabel>

              <Button
                rightIcon={showMeses ? <ChevronUpIcon /> : <ChevronDownIcon />}
                isFullWidth
                justifyContent={'space-between'}
                onClick={() => setShowMeses((current) => !current)}
              >
                Selecione
              </Button>

              <Fade in={showMeses}>
                <Box maxHeight={'30vh'} roundedBottom="md" shadow="md" overflowY={'auto'} p={2} pb={0}>
                  {[12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((mes, index) => {
                    const isSelected = meses.includes(mes);
                    return (
                      <Button
                        disabled={!showMeses}
                        key={mes}
                        isFullWidth
                        variant={'ghost'}
                        justifyContent={'flex-start'}
                        borderBottom={'2px solid #EBEBEB'}
                        rounded={'none'}
                        color={isSelected ? '#5AB2AE' : 'inherit'}
                        p={0}
                        onClick={() => {
                          if (isSelected) {
                            setMeses((current) => current.filter((item) => item !== mes));
                          } else {
                            setMeses((current) => [...current, mes]);
                          }
                        }}
                      >
                        {getMonthName(mes)}
                      </Button>
                    );
                  })}
                </Box>
              </Fade>
            </FormControl>
          </Flex>
        </DrawerBody>

        <DrawerFooter>
          <Flex flexDirection={'column'} gap={4} width={'100%'}>
            <Text fontSize={'sm'} textAlign={'center'}>
              Você pode selecionar até 9 meses
            </Text>
            <Button
              isFullWidth
              disabled={!meses.length || meses.length >= 9}
              variant={'solid'}
              bgColor={'#5AB2AE'}
              color={'white'}
              rounded={'xl'}
              onClick={() => {
                setValue(meses);
                disclosure.onClose();
              }}
            >
              Filtrar
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function PagamentosMes({ mes, parcelas }) {
  const { user } = useContext(AccountContext);
  return (
    <Box key={mes} borderBottom={'2px solid #EBEBEB'} pb={4}>
      <Text color={'#5AB2AE'} fontWeight={'bold'} fontSize={'lg'}>
        {mes}
      </Text>
      {parcelas.map((parcela) => {
        return (
          <Grid templateColumns="2fr 1fr" mt={4} key={parcela.paciente_contrato_parcelas_id}>
            <Box>
              <Text fontSize={'sm'}>Assinatura Plano {user.descricao_plano}</Text>
              <Text fontSize={'xs'} color={'#585858'}>
                Forma de pagamento: {parcela.forma_pagamento_fatura}
              </Text>
              <Text fontSize={'xs'} color={'#585858'}>
                {[12, 15].includes(Number(parcela.forma_pagamento_id))
                  ? !parcela.parcela || parcela.parcela === '0' || parcela.parcela === 0 || parcela.parcela === 1
                    ? ` `
                    : 'Parcelado em ' + parcela.parcela + 'x'
                  : ''}
              </Text>
              <Text fontSize={'xs'} color={'#585858'}>
                Vencimento: {new Date(parcela.data).toLocaleDateString('pt-BR')}
              </Text>
              <Text fontSize={'xs'} color={'#585858'}>
                Pagamento: {new Date(parcela.data_pagamento).toLocaleDateString('pt-BR')}
              </Text>

              {parcela.id_armazenamento_arm && (
                <BaixarNotaFiscalButton contrato={user.id_contrato} pacienteContratoParcelasId={parcela.paciente_contrato_parcelas_id} />
              )}
            </Box>
            <Center>
              <Text color={'#585858'} fontWeight={'bold'}>
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(parcela.valor))}
              </Text>

            </Center>
          </Grid>
        );
      })}
    </Box>
  );
}

function ParcelasPagasList(props) {
  const { user } = useContext(AccountContext);
  const [parcelas, setParcelas] = useState(props.parcelas);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [ano, setAno] = useState(null);
  const [meses, setMeses] = useState([]);

  const disclosureAno = useDisclosure();
  const disclosureMes = useDisclosure();

  const anosParcelas = useMemo(() => {
    const anos = parcelas.map((parcela) => new Date(parcela.data_pagamento).getFullYear());
    return [...new Set(anos)];
  }, [parcelas]);

  const filteredParcelas = useMemo(() => {
    let filtered = parcelas;

    try {
      if (ano) {
        filtered = filtered.filter((parcela) => {
          return parcela.data_pagamento.includes(ano);
        });
      }

      if (meses.length) {
        filtered = filtered.filter((parcela) => {
          const [_, mes] = parcela.data_pagamento.split('-');
          return meses.includes(Number(mes));
        });
      }

      if (search) {
        filtered = filtered.filter((parcela) => {
          const nomeMes = getMonthName(new Date(parcela.data_pagamento).getMonth() + 1);
          const searchToLower = search.toLowerCase();
          return (
            nomeMes.toLowerCase().includes(searchToLower) ||
            parcela.forma_pagamento?.toLowerCase().includes(searchToLower) ||
            new Date(parcela.data_pagamento).toLocaleDateString('pt-BR').includes(search) ||
            parcela.valor.toLowerCase().includes(searchToLower)
          );
        });
      }
    } catch (error) {
      console.error(error);
      return [];
    }

    return filtered;
  }, [ano, meses, parcelas, search]);

  const groupedParcelas = useMemo(() => {
    const groups = new Map();
    filteredParcelas.forEach((parcela) => {
      const key = `${getMonthName(new Date(parcela.data_pagamento).getMonth() + 1)} ${new Date(parcela.data_pagamento).getFullYear()}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(parcela);
    });
    return groups;
  }, [filteredParcelas]);

  const loadData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getParcelasPagas(user.id_contrato);
      setParcelas(data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [user.id_contrato]);

  /*useEffect(() => {
    loadData();
  }, [loadData]); */

  if (loading) {
    return (
      <Box>
        <Flex alignItems={'center'} gap={2} m={8} mb={4}>
          <Input as={Skeleton} rounded={'full'} disabled readOnly />
        </Flex>
        <Flex px={8} pb={4} gap={4} borderBottom={'2px solid #EBEBEB'}>
          <Button as={Skeleton} rounded={'full'} variant="outline" color={'transparent'} rightIcon={<ChevronDownIcon />}>
            Ano
          </Button>
          <Button as={Skeleton} rounded={'full'} variant="outline" color={'transparent'} rightIcon={<ChevronDownIcon />}>
            Mês
          </Button>
        </Flex>
        <Flex flexDirection={'column'} gap={8} p={8}>
          {[1, 2, 3].map((_, index) => {
            return (
              <Box key={index} borderBottom={'2px solid #EBEBEB'} pb={4}>
                <Text as={Skeleton} rounded={'xl'} fontWeight={'bold'} fontSize={'lg'} maxW={'50%'}>
                  &nbsp;
                </Text>
                <Grid templateColumns="2fr 1fr" mt={4}>
                  <Box>
                    <Box as={Skeleton} rounded={'xl'}>
                      <Text fontSize={'sm'}>Assinatura Plano Família Total</Text>
                      <Text fontSize={'xs'}>Forma de pagamento: Pix - Pagarme</Text>
                      <Text fontSize={'xs'}>Data: 01/01/2001</Text>
                    </Box>
                    <Button
                      as={Skeleton}
                      disabled
                      mt={2}
                      size={'xs'}
                      rounded={'full'}
                      rightIcon={<DownloadIcon />}
                      // onClick={disclosureAno.onOpen}
                    >
                      Baixar Nota Fiscal
                    </Button>
                  </Box>
                  <Center>
                    <Text as={Skeleton} rounded={'xl'} fontWeight={'bold'}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(99.99)}
                    </Text>
                  </Center>
                </Grid>
              </Box>
            );
          })}
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Center flexDirection={'column'} gap={8} textAlign={'center'} p={16}>
        <NotFoundWoman></NotFoundWoman>
        <Text fontSize={'lg'} color={'#616161'} fontWeight={'bold'} textAlign={'center'}>
          Não foi possível carregar dados de pagamento
        </Text>
        <Text color={'#616161'} fontSize={'sm'} textAlign={'center'}>
          Por favor, tente novamente ou entre em contato com nosso suporte
        </Text>
        <Button isFullWidth onClick={() => loadData()}>
          Tentar novamente
        </Button>
      </Center>
    );
  }

  return !parcelas.length ? (
    <Center flexDirection={'column'} gap={8} textAlign={'center'} p={16}>
      <Image src={SadIcon} h="12em" />
      <Text fontSize={'lg'} color={'#5AB2AE'} fontWeight={'bold'} textAlign={'center'}>
        Não foi encontrado registro de pagamento
      </Text>
      <Text color={'#5AB2AE'} fontSize={'sm'} textAlign={'center'}>
        Tente novamente mais tarde ou contacte nosso suporte
      </Text>
      <Button isFullWidth onClick={() => loadData()}>
        Tentar novamente
      </Button>
    </Center>
  ) : (
    <Box>
      <Flex alignItems={'center'} gap={2} m={8} mb={4}>
        <InputGroup width={isSearchFocused || search ? '75%' : '100%'} transition={'width 0.3s'}>
          <InputRightElement pointerEvents="none" children={<Search2Icon color="#5AB2AE" />} />
          <Input
            colorScheme="orange"
            focusBorderColor="#5AB2AE"
            variant="filled"
            bgColor={'#F5F5F5'}
            type="search"
            placeholder="Buscar pagamento"
            rounded={'full'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onFocus={() => {
              setIsSearchFocused(true);
            }}
            onBlur={() => {
              setIsSearchFocused(false);
            }}
          />
        </InputGroup>
        <Box width={isSearchFocused || search ? '25%' : 0} overflow={'hidden'} transition={'width 0.3s'} boxSizing="border-box">
          <Button
            isFullWidth
            variant="outline"
            rounded={'full'}
            color={'#5AB2AE'}
            fontSize={'xs'}
            onClick={() => {
              setSearch('');
            }}
          >
            Cancelar
          </Button>
        </Box>
      </Flex>

      <Box
        display={'flex'}
        overflow={'visible'}
        overflowY={'auto'}
        overscrollBehaviorX={'contain'}
        scrollSnapType={'x mandatory'}
        px={8}
        pb={4}
        gap={4}
        borderBottom={'2px solid #EBEBEB'}
      >
        {(ano || !!meses.length) && (
          <Button
            flexShrink={0}
            variant="outline"
            rounded={'full'}
            colorScheme="orange"
            borderColor={'#5AB2AE'}
            color={'#5AB2AE'}
            onClick={() => {
              setAno(null);
              setMeses([]);
            }}
          >
            Limpar filtros
          </Button>
        )}

        <Button
          flexShrink={0}
          variant="outline"
          rounded={'full'}
          colorScheme="orange"
          borderColor={'#5AB2AE'}
          color={ano ? 'white' : '#5AB2AE'}
          bgColor={ano ? '#5AB2AE' : 'white'}
          rightIcon={<ChevronDownIcon />}
          onClick={disclosureAno.onOpen}
        >
          Ano{ano ? `: ${ano}` : null}
        </Button>
        <Button
          flexShrink={0}
          variant="outline"
          rounded={'full'}
          colorScheme="orange"
          borderColor={'#5AB2AE'}
          color={meses.length ? 'white' : '#5AB2AE'}
          bgColor={meses.length ? '#5AB2AE' : 'white'}
          rightIcon={<ChevronDownIcon />}
          onClick={disclosureMes.onOpen}
        >
          Mês{meses.length === 1 ? `: ${getMonthName(meses.at(0))}` : null}
        </Button>
      </Box>

      <Flex flexDirection={'column'} gap={8} p={8}>
        {groupedParcelas.size ? (
          [...groupedParcelas.entries()].map(([mes, parcelas]) => <PagamentosMes key={mes} mes={mes} parcelas={parcelas} />)
        ) : (
          <Center bg={'orange.50'} textAlign={'center'} p={8} rounded={'2xl'}>
            <Text color={'#5AB2AE'} fontWeight={'bold'} fontSize={'sm'}>
              Não foi encontrado registro associado à pesquisa
            </Text>
          </Center>
        )}
      </Flex>

      <DrawerSelectAno disclosure={disclosureAno} setValue={setAno} anosOptions={anosParcelas} />
      <DrawerSelectMes disclosure={disclosureMes} setValue={setMeses} />
    </Box>
  );
}

export default function HistoricoPagamentos() {
  const { setPage } = useContext(AccountContext);
  const state = useLocation()
  console.log("state ", state.state.parcelas)
  useEffect(() => {
    setPage('Histórico de pagamento');
  }, [setPage]);

  return (
    <>
      <ChildHeader />

      <ParcelasPagasList parcelas={state?.state?.parcelas ? state?.state?.parcelas : []}/>
    </>
  );
}
